import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import { TypeAnimation } from "react-type-animation";
import Breadcrumb from "../components/Breadcrumb";
import "./Pages.css";
import "./Team.css";

export const teamMembers = [
  {
    id: "ameet-parikh",
    name: "Ameet Parikh",
    title: "Founder & Partner",
    bio: "Ameet Parikh is the Founder and Partner at Morphis, bringing over three decades of distinguished experience in professional services, strategic consulting, and corporate governance. Known for his expertise in family business advisory, mergers and acquisitions (M&A), and operational scaling, Ameet previously co-founded Tranzmute Capital and established Axis Risk Consulting, which was later acquired by Genpact. His foundational career includes serving as a senior partner and leadership team member at Arthur Andersen and Ernst & Young. A Chartered Accountant and a law graduate from Mumbai University, he also lends his strategic insights to several corporate boards as an independent director, driving sustainable growth and robust governance across diverse industries.",
    tags: ["Family Business Advisory", "M&A", "Corporate Governance"],
    image: "/assets/team/ameet-parikh.webp",
    short_image: "/assets/team/ameet-parikh.webp",
    shortDescription:
      "Veteran business advisor with global risk consulting expertise.",
    linkedin: "https://www.linkedin.com/in/ameet-parikh-30407222/",
  },
  {
    id: "rahul-kapani",
    name: "Rahul Kapani",
    title: "Founder & Partner",
    bio: "Rahul is a Sydenham College graduate and a Chartered Accountant by training. He began his career in 1994, working with large organizations such as Andersen, WNS, Genpact, and True North Private Equity, building a strong foundation in how businesses operate at scale and across cycles. In 2019, he founded Morphis Management Services and now works as a deal maker and advisor. He partners closely with Private Equity firms and family-owned businesses, helping them navigate transactions, growth decisions, and complex transitions. Rahul thrives at the intersection of strategy, numbers, and people, and the relationship-driven nature of advisory work.",
    tags: ["Private Equity", "Deal Advisory", "Value Creation"],
    image: "/assets/team/1_rahul-kapani.webp",
    short_image: "/assets/team/rahul-kapani.webp",
    shortDescription:
      "Private equity advisor driving complex business transactions.",
    linkedin: "https://www.linkedin.com/in/rahulkapani/",
  },
  {
    id: "vimal-taparia",
    name: "Vimal Taparia",
    title: "Partner",
    bio: "Vimal is a qualified Chartered Accountant and has over 18 years of experience spanning private equity, corporate finance, risk advisory, and business management, with prior roles at True North Private Equity, Meru Mobility Tech (where he rose to CFO), Ernst & Young, and Goldman Sachs. A founding member of Morphis in 2019, Vimal co-leads the firm's work with VC/PE funds, founders, and CXOs across the investment lifecycle, from deal screening and execution to post-investment value creation, IPO readiness, and exit planning. He also advises founders and corporates on fundraising, capital structuring, M&A, and monetisation.",
    tags: ["IPO Readiness", "M&A", "Capital Structuring"],
    image: "/assets/team/1_vimal-taparia.webp",
    short_image: "/assets/team/vimal-taparia.webp",
    shortDescription: "Corporate finance leader specializing in IPO readiness.",
    linkedin: "https://www.linkedin.com/in/vimaltaparia22/",
  },
];

// IntersectionObserver hook
function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

const About = () => {
  const teamHeaderRef = useRef(null);
  const [showPartners, setShowPartners] = useState(false);

  const [bodyRef, bodyVisible] = useReveal(0.2);
  const [listRef, listVisible] = useReveal(0.2);
  const [closingRef, closingVisible] = useReveal(0.2);
  const [item1Ref, item1Visible] = useReveal(0.1);
  const [item2Ref, item2Visible] = useReveal(0.1);

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
    });

    AOS.refresh();

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShowPartners(true);
        }
      },
      { threshold: 0.4 },
    );

    if (teamHeaderRef.current) {
      observer.observe(teamHeaderRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Breadcrumb />

      {/* Hero Section */}
      <header className="page-hero" data-aos="fade-right">
        <div className="museum-wrap">
          <span className="section-label">ABOUT US</span>
          <h1>
            Your
            <span className="highlight" style={{ fontStyle: "italic" }}>
              <TypeAnimation
                sequence={[" Execution Partner"]}
                speed={250}
                cursor={false}
              />
            </span>
          </h1>
        </div>
      </header>

      {/* About Content Section */}
      <section className="about-section">
        <div className="museum-wrap">
          <div className="about-content-wrapper">
            {/* Left side - paragraphs */}
            <div 
              ref={bodyRef}
              className={`about-text-content ${bodyVisible ? 'revealed' : ''}`}
            >
              <p  data-aos="fade-right">
                Founded in 2019, Morphis was established by professionals who
                were part of a large private equity operations team, with the
                vision of bringing institutional-quality execution and operating
                support to PE/VC funds and portfolio companies.
              </p>
              <p  data-aos="fade-right">
                Morphis works closely with investors, founders, and management
                teams across two core areas: Transaction Support and Value
                Creation.
              </p>
            </div>

            {/* Right side - Feature cards */}
            <div 
              ref={listRef}
              className={`about-features-wrapper ${listVisible ? 'revealed' : ''}`}
            >
              <div className="about-feature-grid">
                {/* Card 1 */}
                <div
                  ref={item1Ref}
                  className={`about-feature-card ${item1Visible ? 'revealed' : ''}`}
                >
                  <div className="about-feature-icon-box" data-aos="fade-left">
                    <img
                      src="/assets/service/innovation.svg"
                      alt="Innovation"
                      className="about-feature-icon"
                    />
                  </div>
                  <div className="about-feature-content" data-aos="fade-left">
                    <p>
                      End-to-end transaction support across the investment
                      lifecycle
                    </p>
                  </div>
                </div>

                {/* Card 2 */}
                <div
                  ref={item2Ref}
                  className={`about-feature-card ${item2Visible ? 'revealed' : ''}`}
                >
                  <div className="about-feature-icon-box"   data-aos="fade-left">
                    <img
                      src="/assets/service/people.svg"
                      alt="People"
                      className="about-feature-icon"
                    />
                  </div>
                  <div className="about-feature-content" data-aos="fade-left">
                    <p>
                      Value creation initiatives within portfolio companies
                      through both strategic and tactical engagements
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Closing Banner */}
          <div
            ref={closingRef}
            className={`closing-banner ${closingVisible ? 'revealed' : ''}`}
          >
            <div className="closing-banner-bg"></div>
            <div className="closing-banner-inner">
              <div className="closing-banner-left">
                <h2 className="closing-banner-title" data-aos="fade-right" data-aos-delay="100">
                  Transforming Complexity {<br />}
                  <span className="highlight" style={{ fontStyle: "italic" }}>
                    <TypeAnimation
                      sequence={[" Into Clarity"]}
                      speed={250}
                      cursor={false} 
                    />
                  </span>
                </h2>
              </div>
              <div className="closing-banner-right" data-aos="fade-left" data-aos-delay="200">
                <div className="closing-banner-card">
                  <p>
                    Over the years, the firm has supported multiple PE/VC and credit
                    funds across transactions, IPOs, portfolio monitoring, and
                    business management mandates.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <header
        ref={teamHeaderRef}
        className="page-team-about"
        data-aos="fade-up"
      >
        <div className="museum-wrap">
          <h2>
            Meet the{" "}
            <span className="highlight" style={{ fontStyle: "italic" }}>
              {showPartners && (
                <TypeAnimation
                  sequence={["Leadership"]}
                  speed={250}
                  cursor={false}
                />
              )}
            </span>
          </h2>
        </div>
      </header>

      <section className="team-grid-section">
        <div className="museum-wrap">
          <div className="team-grid--three-cards">
            {teamMembers.map((member, index) => {
              return (
                <Link
                  to={`/team/${member.id}`}
                  key={member.id}
                  className="member-card-link"
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                >
                  <div className="member-card">
                    <div className="member-photo" style={{ width: "140px", height: "140px" }}>
                      <img src={member.short_image} alt={member.name} />
                      <div className="member-arrow-circle">
                        <i className="fas fa-plus"></i>
                      </div>
                    </div>
                    <div className="member-header">
                      <div>
                        <span className="member-name-inline">
                          {member.name}
                        </span>
                        <div className="member-designation">{member.title}</div>
                      </div>
                    </div>
                    <div className="member-card-body">
                      {/*<p className="member-card-bio">
                        {member.shortDescription ||
                          (member.bio
                            ? member.bio.substring(0, 120)
                            : "No bio available")}
                      </p>*/}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};

export default About;